// src/pages/AdminPage.jsx
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { adminAPI } from '../services/api';
import { Modal, Spinner, LoadingOverlay, EmptyState, toast } from '../components/UI';

function RoleBadge({ role }) {
  const cfg = { Dispatcher: { bg: '#EFF6FF', color: '#1D4ED8' }, Rider: { bg: '#F0FDF4', color: '#166534' } };
  const c = cfg[role] || { bg: 'var(--gray-100)', color: 'var(--gray-600)' };
  return <span style={{ padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: c.bg, color: c.color }}>{role}</span>;
}

function StatusDot({ active }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: active ? 'var(--success)' : 'var(--danger)' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? 'var(--success)' : 'var(--danger)', display: 'inline-block' }} />
      {active ? 'Active' : 'Suspended'}
    </span>
  );
}

function CreateUserModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', phone: '', password: '', role: 'Dispatcher' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (form.password.length < 6) e.password = 'Minimum 6 characters';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await adminAPI.createUser(form);
      toast.success(`${form.role} account created!`);
      setForm({ name: '', phone: '', password: '', role: 'Dispatcher' });
      onCreated();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.error || 'Failed to create user.'); }
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="👤 Create Account"
      footer={<><button className="btn btn-ghost btn-md" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-md" onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner size={16} /> : 'Create Account'}</button></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-group">
          <label className="form-label">Role</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['Dispatcher', 'Rider'].map(r => (
              <button key={r} type="button" onClick={() => set('role', r)} style={{
                flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                border: `2px solid ${form.role === r ? 'var(--accent)' : 'var(--gray-200)'}`,
                background: form.role === r ? 'var(--accent-glow)' : 'var(--white)',
                color: form.role === r ? 'var(--primary)' : 'var(--gray-500)', transition: 'all 0.15s',
              }}>{r === 'Dispatcher' ? '📋 Dispatcher' : '🚴 Rider'}</button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Full Name</label>
          <input className={`form-input ${errors.name ? 'error' : ''}`} placeholder="e.g. John Doe"
            value={form.name} onChange={e => set('name', e.target.value)} />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input className={`form-input ${errors.phone ? 'error' : ''}`} placeholder="+234 801 234 5678" type="tel"
            value={form.phone} onChange={e => set('phone', e.target.value)} />
          {errors.phone && <span className="form-error">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Password</label>
          <div style={{ position: 'relative' }}>
            <input className={`form-input ${errors.password ? 'error' : ''}`} type={showPwd ? 'text' : 'password'}
              placeholder="Min 6 characters" value={form.password} onChange={e => set('password', e.target.value)}
              style={{ paddingRight: 40 }} />
            <button type="button" onClick={() => setShowPwd(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>
          {errors.password && <span className="form-error">{errors.password}</span>}
        </div>
      </div>
    </Modal>
  );
}

function ResetPwdModal({ open, onClose, user }) {
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (pwd.length < 6) { toast.error('Minimum 6 characters.'); return; }
    setLoading(true);
    try { await adminAPI.resetPassword(user.id, pwd); toast.success('Password reset.'); setPwd(''); onClose(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed.'); }
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`🔑 Reset Password — ${user?.name}`}
      footer={<><button className="btn btn-ghost btn-md" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-md" onClick={handleReset} disabled={loading}>
          {loading ? <Spinner size={16} /> : 'Reset Password'}</button></>}>
      <div className="form-group">
        <label className="form-label">New Password</label>
        <input className="form-input" type="text" placeholder="Min 6 characters"
          value={pwd} onChange={e => setPwd(e.target.value)} />
      </div>
    </Modal>
  );
}

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  const fetchUsers = async () => {
    try { setUsers(await adminAPI.getUsers()); }
    catch { toast.error('Failed to load users.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const setAL = (id, v) => setActionLoading(p => ({ ...p, [id]: v }));

  const handleSuspend = async (u) => {
    setAL(u.id, true);
    try { await adminAPI.suspendUser(u.id); toast.success(`${u.name} suspended.`); await fetchUsers(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed.'); }
    finally { setAL(u.id, false); }
  };

  const handleActivate = async (u) => {
    setAL(u.id, true);
    try { await adminAPI.activateUser(u.id); toast.success(`${u.name} activated.`); await fetchUsers(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed.'); }
    finally { setAL(u.id, false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.name}'s account permanently? This cannot be undone.`)) return;
    setAL(u.id, true);
    try { await adminAPI.deleteUser(u.id); toast.success('Account deleted.'); await fetchUsers(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed.'); }
    finally { setAL(u.id, false); }
  };

  const filtered = users.filter(u => !filterRole || u.role === filterRole);
  const dispatchers = users.filter(u => u.role === 'Dispatcher').length;
  const riders = users.filter(u => u.role === 'Rider').length;
  const suspended = users.filter(u => !u.is_active).length;

  if (loading) return <LoadingOverlay message="Loading users…" />;

  return (
    <div className="animate-fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1 className="page-title">User Management</h1>
          <p className="page-sub">Admin control panel · {users.length} accounts</p>
        </div>
        <button className="btn btn-primary btn-md" onClick={() => setShowCreate(true)}>+ Create Account</button>
      </div>

      <div className="stat-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Dispatchers', value: dispatchers, cls: 'info', icon: '' },
          { label: 'Riders', value: riders, cls: 'success', icon: '' },
          { label: 'Suspended', value: suspended, cls: 'danger', icon: '' },
          { label: 'Total Accounts', value: users.length, cls: 'accent', icon: '' },
        ].map((s, i) => (
          <div key={s.label} className={`stat-card ${s.cls} animate-fade-up delay-${i + 1}`}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-icon">{s.icon}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[{ v: '', l: 'All' }, { v: 'Dispatcher', l: 'Dispatchers' }, { v: 'Rider', l: 'Riders' }].map(o => (
          <button key={o.v} className={`btn btn-sm ${filterRole === o.v ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={() => setFilterRole(o.v)}>{o.l}</button>
        ))}
      </div>

      <div className="card">
        {filtered.length === 0
          ? <EmptyState icon="👤" title="No accounts" subtitle="Create the first account above." />
          : <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Name</th><th>Phone</th><th>Role</th><th>Status</th>
                <th>Active Orders</th><th>Delivered</th><th>Joined</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.6 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.is_active ? 'var(--primary)' : 'var(--gray-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, color: u.is_active ? 'var(--accent)' : 'var(--gray-500)', flexShrink: 0 }}>
                          {u.name?.charAt(0)}
                        </div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</span>
                      </div>
                    </td>
                    <td className="td-mono" style={{ fontSize: 12 }}>{u.phone}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td><StatusDot active={u.is_active} /></td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--info)' }}>{u.active_orders || 0}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--success)' }}>{u.delivered_orders || 0}</td>
                    <td className="td-mono" style={{ fontSize: 11, color: 'var(--gray-400)' }}>{moment(u.created_at).format('MMM D, YYYY')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {u.is_active
                          ? <button className="btn btn-ghost btn-sm" style={{ color: 'var(--warning)', borderColor: 'var(--warning)' }}
                            onClick={() => handleSuspend(u)} disabled={actionLoading[u.id]}>
                            {actionLoading[u.id] ? <Spinner size={12} /> : 'Suspend'}
                          </button>
                          : <button className="btn btn-success btn-sm" onClick={() => handleActivate(u)} disabled={actionLoading[u.id]}>
                            {actionLoading[u.id] ? <Spinner size={12} /> : 'Activate'}
                          </button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => setResetTarget(u)}>Reset Pwd</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                          onClick={() => handleDelete(u)} disabled={actionLoading[u.id]}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={fetchUsers} />
      <ResetPwdModal open={!!resetTarget} onClose={() => setResetTarget(null)} user={resetTarget} />
    </div>
  );
}